import * as LodashFP from 'lodash/fp'
import * as yup from 'yup'

import update from 'immutability-helper'
import {
  appError,
  HttpCommand,
  resultError,
  resultSuccess,
} from '../../interfaces/base'
import {
  CanEdit,
  CreateCreative,
  DoCreativeAction,
  DoCreativesAction,
  FinalizeCreative,
  GetCreative,
  GetCreativeBlueprint,
  GetCreativeForPreview,
  GetCreativePlacements,
  GetCreativePreview,
  GetCreativeThumbnail,
  GetCreativeVersion,
  GetFormatsetPlacementForCreative,
  GetPreviewlink,
  ImportCreatives,
  ICreativeService,
  SetCreativeComment,
  UpdateCardPosition,
  UpdateCreative,
  ValidateCreative,
} from '../../interfaces/services/creatives'
import { decode, doHttp } from '../../utils/json-api'

import { FormatsetId } from '../../interfaces/entities/formatset'

import { Schema } from 'yup'
import adapters from './adapters'

import { Optionset } from '../../interfaces/components/options'
import { CreativeId } from '../../interfaces/entities/creative'
import { appErrorFromAnything } from '../../utils/error'
import { getOption } from '../../utils/get-option'

import defaultThumbnail from '../../assets/images/no-IMG.png'
import { emptyFormGuidelines } from '../../interfaces/components/dynamic-form'
import { IBlueprintService } from '../../interfaces/services/blueprints'
import { IFilesService } from '../../interfaces/services/files'
import { filterBlueprintPaths } from '../../lib/dynamic-form/helpers'
import { IStringTMap } from '../../types/map'
import { translateWithUserLocale } from '../../utils/i18n'
import { arrayPathUnFlobulator } from '../../utils/object'

type SchemaVersionPair = [Schema<any>, string]
type CreativeSchemas = IStringTMap<SchemaVersionPair[]>

const isPreviewPage = () => {
  const isPreviewPage = window.location.hash.startsWith('#/creative-preview')
  return isPreviewPage
}

const creativesService: (DI: any) => ICreativeService = (DI) => {
  const filesService = () => DI.get('FilesService') as IFilesService
  const blueprintService = () => DI.get('BlueprintService') as IBlueprintService

  const getCreativeForPreview: GetCreativeForPreview = async (id) => {
    const newUrl = !isPreviewPage()
      ? `/api/creatives/${id}?includes=formatset.project,comment`
      : `/api/public/creatives/${id}?includes=formatset.project,comment`
    try {
      const creativeResponse = await doHttp({
        method: 'get',
        url: newUrl,
      })
      const creative = decode(
        adapters.creative,
        creativeResponse,
        'withProject',
        { id },
      )
      return resultSuccess({ creative, public: isPreviewPage() })
    } catch (e) {
      return resultError(appErrorFromAnything(e))
    }
  }

  const getCreativePreviewlink: GetPreviewlink = async (id: string) => {
    try {
      const linkResponse = await doHttp({
        method: 'get',
        url: `/api/creatives/generate_preview_link/${id}`,
      })

      return resultSuccess(linkResponse.meta.preview_links)
    } catch (e) {
      return resultError(appErrorFromAnything(e))
    }
  }

  const getCreative: GetCreative = async (id) => {
    try {
      const creativeResponse = await doHttp({
        method: 'get',
        url: `/api/creatives/${id}?includes=formatset.project,comment`,
      })

      const creative = decode(
        adapters.creative,
        creativeResponse,
        'withProject',
        { id },
      )

      const guidelinesResponse =
        await blueprintService().getCreativeGuidelines(id)
      const guidelines =
        guidelinesResponse.type === 'error'
          ? emptyFormGuidelines()
          : guidelinesResponse.payload

      const blueprintResponse = await doHttp({
        method: 'get',
        url: `/api/creatives/${creativeResponse.data.id}/blueprint`,
      })
      const blueprint = decode(
        adapters.blueprint,
        blueprintResponse,
        'default',
        { name: 'creative-blueprint' },
      )
      const creativeWithBlueprintAndGuidelines = update(creative, {
        blueprint: { $set: blueprint },
        guidelines: { $set: guidelines },
      })
      return resultSuccess(creativeWithBlueprintAndGuidelines)
    } catch (e) {
      return resultError(appErrorFromAnything(e))
    }
  }

  const getCreativeBlueprint: GetCreativeBlueprint = async (
    creativeId,
    type,
  ) => {
    try {
      const blueprintResponse = await doHttp({
        method: 'get',
        url: `/api/creatives/${creativeId}/blueprint`,
      })

      const blueprint = decode(
        adapters.blueprint,
        blueprintResponse,
        'default',
        { name: `${type}-creative` },
      )
      return resultSuccess(blueprint)
    } catch (e) {
      return resultError(appErrorFromAnything(e))
    }
  }

  const createCreative: CreateCreative = async (params) => {
    try {
      const response = await doHttp({
        method: 'post',
        url: 'api/creatives',
        data: {
          formatset_id: params.formatsetId,
          creative_type: params.creativeType,
          creative: {},
          name: `${params.formatsetName}_${params.creativeType}`,
        },
      })

      const adapted = decode(adapters.creative, response)
      return resultSuccess(adapted)
    } catch (e) {
      return resultError(appErrorFromAnything(e))
    }
  }

  const updateCreative: UpdateCreative = async (params) => {
    try {
      const response = await doHttp({
        method: 'put',
        url: `api/creatives/${params.id}`,
        data: {
          formatset_id: params.formatsetId,
          creative_type: params.type,
          creative: params.data,
          name: params.name,
        },
      })

      const adapted = decode(adapters.creative, response)
      return resultSuccess(adapted)
    } catch (e) {
      return resultError(appErrorFromAnything(e))
    }
  }

  const finalizeCreative: FinalizeCreative = async (params) => {
    try {
      const response = await doHttp(
        {
          method: 'put',
          url: `api/creatives/${params.id}/finalize`,
          data: {
            formatset_id: params.formatsetId,
            creative_type: params.type,
            creative: params.data,
            name: params.name,
          },
        },
        { expectedErrorCodes: [422, 400] },
      )

      if (response.data.valid === false) {
        const adaptedFormValidation = decode(adapters.formValidation, {
          ...response,
          blueprint: params.blueprint,
        })

        return resultError({ validation: adaptedFormValidation, message: null })
      } else {
        if (!('errors' in response.data)) {
          const adapted = decode(adapters.creative, response)
          return resultSuccess(adapted)
        }
      }
      return resultError({
        validation: {
          validUntil: 0,
          fields: {},
        },
        message: LodashFP.flattenDeep(response.data.errors).join(','),
      })
    } catch (e) {
      return resultError({
        validation: {
          validUntil: 0,
          fields: {},
        },
        message: null,
      })
    }
  }

  const importCreatives: ImportCreatives = async (
    formatsetId: FormatsetId,
    ids: CreativeId[],
  ) => {
    try {
      const response = await doHttp({
        method: 'post',
        url: `api/formatsets/${formatsetId}/import_creatives`,
        data: {
          ids: ids,
        },
      })
      return resultSuccess(response)
    } catch (e) {
      return resultError({
        statusCode: '500',
        title: translateWithUserLocale('whoops') + '!',
        message: translateWithUserLocale('error.import_creatives'),
      })
    }
  }

  const validateCreative: ValidateCreative = async (params) => {
    try {
      const response = await doHttp(
        {
          method: 'put',
          url: `api/creatives/${params.id}/finalize?validate=true`,
          data: {
            formatset_id: params.formatsetId,
            creative_type: params.type,
            creative: params.data,
            name: params.name,
          },
        },
        { expectedErrorCodes: [422, 400] },
      )

      if (response.data.valid === false) {
        const adaptedFormValidation = decode(adapters.formValidation, {
          ...response,
          blueprint: params.blueprint,
        })

        return resultError({ validation: adaptedFormValidation, message: null })
      } else {
        if (!('errors' in response.data)) {
          const adapted = decode(adapters.creative, response)
          return resultSuccess(adapted)
        }
      }
      return resultError({
        validation: {
          validUntil: 0,
          fields: {},
        },
        message: LodashFP.flattenDeep(response.data.errors).join(','),
      })
    } catch (e) {
      return resultError({
        validation: {
          validUntil: 0,
          fields: {},
        },
        message: null,
      })
    }
  }

  const doCreativeAction: DoCreativeAction = async (action, creative) => {
    try {
      const target: HttpCommand = LodashFP.cond([
        [
          (_) => action.name === 'duplicate',
          (_) =>
            ({
              method: 'post',
              url: `/api/copy_creative/${creative.id}`,
              data: {},
            }) as HttpCommand,
        ],
        [
          (_) => action.name === 'broadcast',
          (_) =>
            ({
              method: 'post',
              url: `/api/creatives/${creative.id}/broadcast`,
              data: {},
            }) as HttpCommand,
        ],
        [
          (_) => action.name !== 'duplicate' && action.name !== 'broadcast',
          (_) =>
            ({
              method: 'post',
              url: '/api/creatives/change_state',
              data: {
                transition: action.name,
                ids: [creative.id],
              },
            }) as HttpCommand,
        ],
      ])(action)

      const response = await doHttp(target)

      if (response.status) {
        return resultError(
          appError({
            message: LodashFP.flattenDeep(response.data.errors).join(','),
          }),
        )
      } else {
        const decoded = LodashFP.cond([
          [
            (_) => action.name === 'duplicate',
            (_) => decode(adapters.creative, response, 'default'),
          ],
          [
            (_) => action.name !== 'duplicate',
            (_) =>
              decode(adapters.creative, response, 'default', {
                id: creative.id,
              }),
          ],
        ])(action)

        const notificationDialogMessage: Optionset = [
          {
            work_in_progress: translateWithUserLocale(
              'notifications.creative_set_to_wip',
              {
                name: decoded.name,
              },
            ),
            archived: translateWithUserLocale(
              'notifications.creative_set_to_archived',
              { name: decoded.name },
            ),
            deleted: translateWithUserLocale('notifications.creative_deleted', {
              name: decoded.name,
            }),
            sent_to_agency: translateWithUserLocale(
              'notifications.creative_sent_to_agency',
              { name: decoded.name },
            ),
            specification_finalized: translateWithUserLocale(
              'notifications.creative_finalized',
              { name: decoded.name },
            ),
          },
          translateWithUserLocale('success_message.change_status'),
        ]

        const message = LodashFP.cond([
          [
            (_) => action.name === 'duplicate',
            (_) => translateWithUserLocale('success_message.copy_creative'),
          ],
          [
            (_) => action.name === 'broadcast',
            (_) =>
              translateWithUserLocale('success_message.broadcast_creative'),
          ],
          [
            (_) => action.name !== 'duplicate' && action.name !== 'broadcast',
            (_) => getOption(decoded.status.name, notificationDialogMessage),
          ],
        ])(action)

        return resultSuccess({ creative: decoded, message: message, action })
      }
    } catch (e) {
      console.error(e)
      return resultError(appErrorFromAnything(e))
    }
  }

  const doCreativesAction: DoCreativesAction = async (
    action,
    creatives,
    comment,
  ) => {
    try {
      const target: HttpCommand = {
        method: 'post',
        url: '/api/creatives/change_state',
        data: {
          transition: action.name,
          ids: creatives.map((creative) => creative.id),
          comment: comment,
        },
      }

      const response = await doHttp(target)

      if (response.status) {
        return resultError(
          appError({
            message: LodashFP.flattenDeep(response.data.errors).join(','),
          }),
        )
      } else {
        const decoded = decode(
          adapters.creatives,
          response,
          'default',
          creatives.map((creative) => {
            return {
              id: creative.id,
            }
          }),
        )

        return resultSuccess({
          creatives: decoded,
          warnings: response.meta.warnings || [],
        })
      }
    } catch (e) {
      console.error(e)
      return resultError(appErrorFromAnything(e))
    }
  }

  const setCreativeComment: SetCreativeComment = async (
    creativeId: CreativeId,
    comment: string,
  ) => {
    try {
      const response = await doHttp({
        method: 'patch',
        url: `api/creatives/${creativeId}/relationships/comments`,
        data: {
          body: comment,
        },
      })

      const adapted = decode(adapters.comment, response)

      return resultSuccess(adapted)
    } catch (e) {
      return resultError({
        statusCode: '500',
        title: translateWithUserLocale('whoops') + '!',
        message: translateWithUserLocale('error.save_comment'),
      })
    }
  }

  const updateCardPosition: UpdateCardPosition = async (
    creativeId: CreativeId,
    displayOrder: number,
  ) => {
    try {
      const target: HttpCommand = {
        method: 'post',
        url: `/api/creatives/${creativeId}/change_position`,
        data: {
          desired_position: displayOrder,
        },
      }

      const response = await doHttp(target)

      const decoded = decode(adapters.creative, response)
      return resultSuccess(decoded)
    } catch (e) {
      return resultError({
        statusCode: '500',
        title: translateWithUserLocale('whoops') + '!',
        message: translateWithUserLocale('error.save_comment'),
      })
    }
  }

  const getCreativeThumbnail: GetCreativeThumbnail = (creative) => {
    const filePaths = filterBlueprintPaths(
      creative.blueprint,
      (bp) => bp.type === 'file',
    )

    for (const path of filePaths.map(arrayPathUnFlobulator)) {
      const fileId = LodashFP.getOr(null, path, creative.data)
      if (fileId != null) {
        return filesService().getFileThumbnailUrl(fileId)
      }
    }

    return defaultThumbnail
  }

  const getPreview: GetCreativePreview = async (creative, placement) => {
    const newUrl = !isPreviewPage()
      ? `/api/creatives/${creative.id}/preview`
      : `/api/public/creatives/${creative.id}/preview`
    try {
      const target: HttpCommand = {
        method: 'post',
        url: newUrl,
        data: {
          creative: creative.data,
        },
        config: {
          params: {
            format: placement,
          },
        },
      }

      const response = await doHttp(target, {
        throwOnError: true,
        expectedErrorCodes: [500, 422, 404, 400],
      })
      return resultSuccess(response)
    } catch (e) {
      const fallbackErrorMessage = translateWithUserLocale(
        'error.facebook_preview',
      )
      return resultError(appErrorFromAnything(e, fallbackErrorMessage))
    }
  }

  const getFormatsetPlacementForCreative: GetFormatsetPlacementForCreative = (
    creative,
    placement,
  ) => {
    const formatsets = creative?.project?.formatsets

    if (formatsets && formatsets[0]) {
      return LodashFP.getOr(null, [placement], formatsets[0].data.placement)
    }

    return null
  }

  const canEdit: CanEdit = (creative) => {
    const result =
      !LodashFP.includes(creative.status.name, [
        'specification_finalized',
        'archived',
        'sent_to_agency',
        'outdated',
      ]) &&
      (creative.project === undefined ||
        !creative.project.formatsets[0].invalid)
    return result
  }

  const getPlacements: GetCreativePlacements = async (creative) => {
    const newUrl = !isPreviewPage()
      ? `/api/creatives/${creative.id}/preview_positions`
      : `/api/public/creatives/${creative.id}/preview_positions`
    try {
      const response = await doHttp(
        {
          method: 'get',
          url: newUrl,
        },
        {
          throwOnError: true,
          expectedErrorCodes: [404],
        },
      )

      return response
    } catch (e) {
      return []
    }
  }

  const getCreativeVersion: GetCreativeVersion = (creative) => {
    const type = creative.type.name || 'unknown'
    const data = creative.data || {}

    const schemas: CreativeSchemas = {
      video: [
        [
          yup.object({
            file: yup.string().required(),
            thumbnail: yup.string().nullable(),
          }),
          '1',
        ],
      ],

      carousel: [
        [
          yup.object({
            creatives: yup
              .array()
              .of(
                yup.object({
                  file: yup.string().required(),
                  thumbnail: yup.string().nullable(),
                }),
              )
              .required(),
          }),
          '1',
        ],

        [
          yup.object({
            creatives: yup
              .array()
              .of(
                yup.object({
                  image: yup.string().nullable(),
                  video: yup.string().nullable(),
                }),
              )
              .required(),
          }),
          '2',
        ],
      ],
    }

    if (type in schemas) {
      for (const sv of schemas[type]) {
        if (
          sv[0].isValidSync(data, {
            strict: true,
            abortEarly: true,
            stripUnknown: true,
          })
        ) {
          return `${type}_${sv[1]}`
        }
      }
    }

    return `${type}_?`
  }

  return {
    canEdit,
    getCreative,
    getCreativeForPreview,
    getPreview,
    getFormatsetPlacementForCreative,
    getCreativeBlueprint,
    doCreativeAction,
    doCreativesAction,
    createCreative,
    updateCreative,
    importCreatives,
    finalizeCreative,
    validateCreative,
    setCreativeComment,
    updateCardPosition,
    getCreativeThumbnail,
    getPlacements,
    getCreativeVersion,
    getCreativePreviewlink,
  }
}

export default creativesService
